import classNames from "classnames";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckSuccessIcon } from "src/assets/Icons";
import { Divider } from "antd";
import { PAYMENT_METHODS, PaymentOption, PaymentSlotContext } from "../constants";
import { usePaymentReturnType } from "../hooks/usePayment";

const PaymentChip: FC<{
  title: ReactNode;
  subTitle: ReactNode;
  isAllowed: boolean;
  isSelected: boolean;
  onSelect: () => void;
}> = ({
  title,
  subTitle,
  isAllowed,
  isSelected,
  onSelect,
}) => {
  return (
    <button
      className={classNames(
        "border-border-default flex w-full cursor-pointer items-center justify-between rounded-lg border bg-background-default p-6 shadow-sm transition-all duration-200 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50",
        isSelected && "bg-background-active/5! border-border-active! shadow-none!",
      )}
      disabled={!isAllowed}
      onClick={onSelect}
    >
      <span className="flex flex-col items-start">
        <span className="text-base font-semibold md:text-lg">{title}</span>
        <span className="text-font-subtle text-xs font-medium md:text-sm">{subTitle}</span>
      </span>
      <span className="border-border-default flex h-5 w-5 shrink-0 items-center justify-center rounded-full border">
        {isSelected && <CheckSuccessIcon className="text-border-active h-full w-full" />}
      </span>
    </button>
  );
};


const PaymentSlot: FC<PaymentSlotContext> = (props) => {
  const { t } = useTranslation();
  const config = PAYMENT_METHODS[props.selectedPayment.paymentMethod];
  return (
    <div className="flex flex-col gap-3">
      <div className="text-font-subtle text-base font-medium uppercase tracking-wide">{t("Flights.details.payment.youAreAboutToPay")}</div>
      {config.renderBody(props)}
      <div className="text-xs font-medium text-font-danger">{config.footer}</div>
    </div>
  );
};


type PaymentOptionsType = {
  payment:usePaymentReturnType;
  pointsFactor:number;
  currency:string;
  disableAll:boolean;
}

const PaymentOptions: FC<PaymentOptionsType> = ({ payment, currency, pointsFactor, disableAll }) => {
  const { t } = useTranslation();
  const { paymentOptions, selectedPayment, selectPaymentMethod, updateMix, maxMixPoints, mixError } = payment;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-8 rounded-md bg-background-default p-6">
        <div className="flex flex-col">
          <div className="text-font-default text-lg font-medium md:text-2xl">{t("Flights.details.payment.choosePaymentMethod")}</div>
          <div className="text-font-subtle max-w-96 text-xs md:text-sm">{t("Flights.details.payment.choosePaymentMethodSubtitle")}</div>
        </div>
        <div className="flex w-full flex-col gap-2">
          {paymentOptions.map((option: PaymentOption) => {
            const config = PAYMENT_METHODS[option.paymentMethod];
            return (
              <PaymentChip
                isAllowed={option.allowed && !disableAll}
                isSelected={selectedPayment?.paymentMethod === option.paymentMethod}
                onSelect={() => selectPaymentMethod(option.paymentMethod)}
                subTitle={config.subTitle}
                title={config.title}
                key={option.paymentMethod}
              />
            );
          })}
        </div>
        <Divider className="m-0!" orientation="horizontal" />
        <div className="flex w-full flex-col gap-2">
          {selectedPayment && (
            <PaymentSlot
              selectedPayment={selectedPayment}
              currency={currency}
              conversionFactor={pointsFactor}
              maxMixPoints={maxMixPoints}
              mixError={mixError}
              updateMix={updateMix}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentOptions;
